/**
 * AutismPrefillFunctions.js
 *
 * Client-side prefill pipeline for Adult Autism assessment forms. When a patient or
 * informant saves form answers, this module formats those answers into human-readable
 * prefill strings and writes them to the actor's own assessmentStages/history document.
 *
 * Architecture:
 * - Reuses the shared questionTypeHandlers from PrefillFunctions.js (28 formatters)
 * - Defines an autism-specific handler map that overrides a few entries where autism
 *   form questions have different subquestion structures than ADHD equivalents
 * - Implements buildFormDataShimFromInstance() which transforms subcollection-based
 *   form instance data into the existingData.formData shape that findValueInFormData
 *   expects, enabling full handler reuse
 * - Runs prefill asynchronously after each debounced form write (fire-and-forget,
 *   non-blocking to the UI)
 *
 * Key difference from ADHD: In ADHD, prefill runs inside PatientFormTemplate.js on
 * a monolithic patient document. For Autism, prefill runs separately and writes to
 * a dedicated assessmentStages/history subcollection document. Each actor (patient,
 * informant) writes only to their own document — no cross-collection writes.
 */
import { updateDoc, serverTimestamp } from 'firebase/firestore';
import { resolveFormInstanceForDisplay } from '../../../../utils/resolveFormTemplates';
import {
  questionTypeHandlers,
  providerVarHandlerMap,
  findValueInFormData,
  updateDataModelWithPrefill,
} from './PrefillFunctions';

// ─── Autism-specific handler overrides ────────────────────────────────────────

/**
 * Composite handler for autism's socialInteractions question which has 4 subquestions
 * (socialInteractions, socialFatigue, socialMasking, emotionalOverwhelm) compared to
 * ADHD's single-subquestion version. Formats all 4 into a readable multi-line summary.
 *
 * Each value is formatted via the shared selectOne handler (not a bare `.value`
 * read) because the first subquestion has an "Other: please provide details"
 * box — reading `.value` directly dropped the patient's typed detail from the
 * prefill (the same dropped-detail bug class as the medications/otherTexts fix).
 * Parts are joined with a blank line to stay byte-compatible with the server
 * fallback formatter (prefill_formatter.py), so the LLM change-detection layer
 * never sees a spurious diff between client-written and fallback-written values.
 */
const SOCIAL_INTERACTIONS_FIELDS = [
  ['socialInteractions', 'Understanding social cues'],
  ['socialFatigue', 'Social fatigue'],
  ['socialMasking', 'Social masking'],
  ['emotionalOverwhelm', 'Emotional overwhelm'],
];

const socialInteractionsAutismHandler = (answer, providerVarName, allSubquestions, existingData) => {
  const parts = [];

  SOCIAL_INTERACTIONS_FIELDS.forEach(([varName, label]) => {
    const raw = findValueInFormData(existingData, varName) ||
                findValueInFormData(existingData, `informant_${varName}`);
    // selectOne preserves typed detail ("Other: <what the patient typed>") and
    // returns null for anything unrenderable, so raw objects never leak out.
    const display = questionTypeHandlers.selectOne(raw);
    if (display) parts.push(`${label}: ${display}`);
  });

  return parts.length > 0 ? parts.join('\n\n') : null;
};

/**
 * Autism-specific provider variable handler map. Inherits ADHD defaults and overrides
 * where autism form structures differ. The 3 new autism-specific providerVarNames are
 * added here alongside the socialInteractions override.
 */
export const autismProviderVarHandlerMap = {
  ...providerVarHandlerMap,
  socialInteractions: 'socialInteractionsAutism',
  copingStrategies: 'selectMultiple',
  // Form definition is selectMultiple ("Select all that apply"). Mapping this
  // to selectOne caused the handler to return the raw modular
  // {selectedOptions: [...]} object, which was then written into patientPrefill
  // and shown in the review "Patient's report" panel as
  // "{'selectedOptions': ['Frequent tripping or falling']}".
  motorCoordinationDifficulties: 'selectMultiple',
  skillRegression: 'selectOne',
  communicationPreferences: 'selectMultiple',
  sensorySensitivities: 'selectMultiple',
};

/**
 * Extended handler registry that includes the autism-specific composite handler.
 * Falls through to the shared questionTypeHandlers for all standard types.
 */
const autismQuestionTypeHandlers = {
  ...questionTypeHandlers,
  socialInteractionsAutism: socialInteractionsAutismHandler,
};

// ─── Form data shim ──────────────────────────────────────────────────────────

/**
 * Converts the "modular" answer shapes that GenericFormPage writes into the
 * legacy ADHD shapes that the shared prefill handlers (extractValue, composite
 * handlers like sleep/alcohol/drugs) already understand.
 *
 * GenericFormPage stores:
 *   - selectMultiple: { selectedOptions: ["A", "B"], otherTexts?: { A: "detail" } }
 *   - selectOne (with typed detail): { selected: "Other", otherText: "detail" }
 *
 * The ADHD handlers expect:
 *   - selectMultiple: [{ value: "A", otherValue?: "detail" }, { value: "B" }]
 *   - selectOne: { value: "Other", otherValue?: "detail" }
 *
 * Without this conversion, composite handlers silently drop modular answers:
 * their `Array.isArray(...)` guards fail on the { selectedOptions } dict (so
 * e.g. sleep/alcohol/drug "challenges" bullet lists vanish from prefill), and
 * extractValue falls through and returns the raw object (risking
 * "[object Object]" in prefill text). Normalising once here — at the single
 * point where raw answers enter the handler-facing data tree — fixes every
 * composite handler at once without touching the (ADHD-shared) handlers.
 *
 * Legacy/ADHD shapes and plain values pass through untouched.
 */
export function normalizeModularAnswerShape(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;

  // Modular selectMultiple → array of { value, otherValue? }
  if (Array.isArray(value.selectedOptions)) {
    const otherTexts =
      value.otherTexts && typeof value.otherTexts === 'object' && !Array.isArray(value.otherTexts)
        ? value.otherTexts
        : {};
    return value.selectedOptions.map(opt => {
      const typed = otherTexts[opt];
      return typed && typeof typed === 'string'
        ? { value: opt, otherValue: typed }
        : { value: opt };
    });
  }

  // Modular selectOne → { value, otherValue? }. Guard on `'value' in value`
  // so we never reshape an ADHD-format answer that happens to also carry a
  // `selected` key.
  if (value.selected !== undefined && !('value' in value)) {
    return value.otherText
      ? { value: value.selected, otherValue: value.otherText }
      : { value: value.selected };
  }

  return value;
}

/**
 * Transforms an autism form instance (subcollection document) into the
 * existingData.formData shape that findValueInFormData and composite handlers expect.
 *
 * ADHD stores all form answers in a monolithic document:
 *   existingData.formData.{formKey}.formSubsections.{panel}.questions.{q}.subquestions.{sq}.answer
 *
 * Autism stores answers separately from the form definition:
 *   formInstance = { formDefinition: { formSubsections: ... }, answers: { [questionKey]: { [varName]: value } } }
 *
 * This shim reconstructs the ADHD-compatible tree from the autism structure so that
 * findValueInFormData can traverse it transparently. Only the current form instance
 * is needed because all autism composite handlers operate within a single form.
 *
 * @param {string} formKey - The form key (e.g. "yourLifestyle")
 * @param {object} formInstance - The full form instance with formDefinition and answers
 * @returns {object} An existingData-shaped object with .formData property
 */
export function buildFormDataShimFromInstance(formKey, formInstance) {
  if (!formInstance?.formDefinition?.formSubsections) {
    return { formData: {} };
  }

  const { formDefinition, answers = {} } = formInstance;
  const reconstructedSubsections = {};

  for (const [subKey, subsection] of Object.entries(formDefinition.formSubsections)) {
    const reconstructedQuestions = {};

    for (const [qKey, question] of Object.entries(subsection.questions || {})) {
      const questionAnswers = answers[qKey] || {};
      const reconstructedSubquestions = {};

      for (const [sqKey, subquestion] of Object.entries(question.subquestions || {})) {
        const varName = subquestion.serviceUserVarName;
        // Nested answers (production) or flat demo answers keyed by varName
        const answerValue = questionAnswers[varName] !== undefined
          ? questionAnswers[varName]
          : answers[varName];

        reconstructedSubquestions[sqKey] = {
          ...subquestion,
          // Normalise modular shapes to legacy shapes so the shared ADHD
          // handlers (which traverse this tree via findValueInFormData) can
          // read them — see normalizeModularAnswerShape.
          answer: answerValue !== undefined ? normalizeModularAnswerShape(answerValue) : null,
        };
      }

      reconstructedQuestions[qKey] = {
        ...question,
        subquestions: reconstructedSubquestions,
      };
    }

    reconstructedSubsections[subKey] = {
      ...subsection,
      questions: reconstructedQuestions,
    };
  }

  return {
    formData: {
      [formKey]: {
        formSubsections: reconstructedSubsections,
      },
    },
  };
}

// ─── Prefill target lookup ───────────────────────────────────────────────────

/**
 * Locates the target question in the history stage schema by matching the CI
 * sectionTitle and providerVarName. Returns the dot-path prefix for Firestore updates.
 *
 * @param {object} historyData - The assessmentStages/history.data object (sections tree)
 * @param {string} stageName - The CI section title to find (e.g. "Patient Information")
 * @param {string} providerVarName - The target providerVarName within that section
 * @returns {string|null} Dot-path prefix like "data.sections.1.subsections.1.subsectionQuestions.2"
 */
function findPrefillTargetPath(historyData, stageName, providerVarName) {
  if (!historyData?.sections) return null;

  for (const [secKey, section] of Object.entries(historyData.sections)) {
    if (section.sectionTitle !== stageName) continue;

    for (const [subsecKey, subsection] of Object.entries(section.subsections || {})) {
      for (const [qKey, question] of Object.entries(subsection.subsectionQuestions || {})) {
        if (question.providerVarName === providerVarName) {
          return `data.sections.${secKey}.subsections.${subsecKey}.subsectionQuestions.${qKey}`;
        }
      }
    }
  }

  return null;
}

/**
 * True when a prefill value is real, usable content: a non-whitespace string
 * or any other non-null value. Used both to decide whether a newly formatted
 * value is worth writing and whether an already-stored value must be protected
 * from being overwritten.
 */
function isNonEmptyPrefillValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

// ─── Core prefill dispatcher ─────────────────────────────────────────────────

/**
 * Formats a prefill value for a given providerVarName using the autism handler map.
 * Mirrors updateDataModelWithPrefill's dispatch logic but uses autism-specific handlers.
 *
 * @param {string} providerVarName - Target CI field
 * @param {object} answer - The raw answer value for the triggering subquestion
 * @param {object} allSubquestions - All subquestions on the parent form question
 * @param {object} existingData - The formData shim (ADHD-compatible shape)
 * @param {string} formKey - Current form key
 * @param {string} currentPanel - Current subsection key
 * @param {string} currentQuestionId - Current question key
 * @returns {string|null} Formatted prefill string or null
 */
export function formatPrefillValue(providerVarName, answer, allSubquestions, existingData, formKey, currentPanel, currentQuestionId) {
  const handlerType = autismProviderVarHandlerMap[providerVarName] ||
                      autismProviderVarHandlerMap.__default__;

  const handler = autismQuestionTypeHandlers[handlerType];
  if (!handler) return null;

  if (handlerType === 'name') {
    return handler(allSubquestions, existingData, formKey, currentPanel, currentQuestionId);
  }

  if (handlerType === 'concatenateStrings') {
    return handler(answer, providerVarName, allSubquestions, existingData, formKey, currentPanel, currentQuestionId);
  }

  // Composite handlers that need existingData for cross-question lookups
  const compositeHandlers = [
    'previousAndCurrentLegalMatters', 'physicalActivity', 'dietAndNutrition',
    'caffeine', 'sleep', 'alcohol', 'smokingAndTobacco', 'drugsAndOtherSubstances',
    'gambling', 'driving', 'caringResponsibilities', 'partnerRelationship',
    'employmentHistory', 'developmentalMilestones', 'challengesInEducation',
    'complicationsInGivingBirth', 'currentlyPregnantOrBreastfeeding',
    'socialInteractionsAutism',
  ];

  if (compositeHandlers.includes(handlerType)) {
    return handler(answer, providerVarName, allSubquestions, existingData);
  }

  // Standard single-value handlers (selectOne, selectMultiple, freeText, date, etc.)
  return handler(answer, providerVarName);
}

// ─── Main prefill runner ─────────────────────────────────────────────────────

/**
 * Runs prefill for all questions in a form instance that have prefill metadata.
 * Collects Firestore dot-path updates and writes them to the actor's history stage.
 *
 * This function is designed to be called after a debounced form write completes.
 * It re-reads the current form instance state (latest answers) and formats all
 * prefill-eligible questions, writing the results atomically to the history stage.
 *
 * @param {string} formKey - The form key being saved (e.g. "yourLifestyle")
 * @param {object} formInstance - Full form instance (formDefinition + answers)
 * @param {object} historyStageData - Current content of assessmentStages/history.data
 * @param {import('firebase/firestore').DocumentReference} historyStageRef - Firestore ref to the history stage doc
 * @param {string} prefillField - "patientPrefill" or "informantPrefill"
 * @param {string} timestampField - "patientPrefillLastUpdatedAt" or "informantPrefillLastUpdatedAt"
 */
export async function runAutismPrefill(
  formKey,
  formInstance,
  historyStageData,
  historyStageRef,
  prefillField = 'patientPrefill',
  timestampField = 'patientPrefillLastUpdatedAt'
) {
  if (!formInstance?.formDefinition?.formSubsections || !historyStageData) return;

  const { formDefinition, answers = {} } = formInstance;

  // Guard against transient hydration states: a form instance whose answers
  // map is empty means the answers haven't loaded (or the actor hasn't typed
  // anything yet). Running prefill from it can only produce empty values, and
  // before the never-clobber guard existed, doing so could null out good
  // stored prefill. Skip the whole run.
  if (!answers || Object.keys(answers).length === 0) return;

  const existingData = buildFormDataShimFromInstance(formKey, formInstance);
  const updates = {};
  const currentTimestamp = new Date().toISOString();

  // Iterate all subsections and questions looking for prefill metadata
  for (const [subKey, subsection] of Object.entries(formDefinition.formSubsections)) {
    for (const [qKey, question] of Object.entries(subsection.questions || {})) {
      if (!question.prefillsToModules || !question.prefillsToStages || !question.prefillsToProviderVarNames) {
        continue;
      }

      const providerVarNames = Array.isArray(question.prefillsToProviderVarNames)
        ? question.prefillsToProviderVarNames
        : [question.prefillsToProviderVarNames];

      // Process each module/stage pair (always single-element for autism)
      question.prefillsToModules.forEach((moduleKey, moduleIndex) => {
        const stageName = question.prefillsToStages[moduleIndex];
        if (!stageName) return;

        providerVarNames.forEach(providerVarName => {
          const targetPath = findPrefillTargetPath(historyStageData, stageName, providerVarName);
          if (!targetPath) return;

          // Build subquestion map for the handler
          const allSubquestions = question.subquestions || {};

          // Get the "answer" — for composite handlers we pass the first subquestion's answer
          // as the trigger; the handler will look up other values via findValueInFormData.
          // Answers can be stored either nested by question key (answers[qKey][varName],
          // legacy) or FLAT by varName (answers[varName], the shape GenericFormPage
          // writes). We must check both — looking only at the nested shape made every
          // simple handler receive `undefined` for flat-stored forms, so no prefill was
          // ever written for them. This mirrors the lookup already used by the display
          // path (formatHistoryPrefillFromFormInstances).
          const questionAnswers = answers[qKey] || {};
          const firstSubquestion = Object.values(allSubquestions)[0];
          const triggerVarName = firstSubquestion?.serviceUserVarName;
          const triggerAnswer = triggerVarName
            ? (questionAnswers[triggerVarName] ?? answers[triggerVarName] ?? null)
            : null;

          const prefillValue = formatPrefillValue(
            providerVarName,
            triggerAnswer,
            allSubquestions,
            existingData,
            formKey,
            subKey,
            qKey
          );

          // Never-clobber guard: only real values are ever written. Previously
          // a null/empty formatted value was written unconditionally, which
          // could wipe good stored prefill when this runner fired against a
          // partially loaded form (e.g. answers for OTHER questions not yet
          // hydrated, or a formatter returning null for an in-progress
          // answer). The trade-off — a deliberately cleared answer leaves the
          // old prefill in place until re-answered — is accepted: silently
          // destroying good data is far worse, and clinicians can always edit
          // the proforma value directly.
          if (!isNonEmptyPrefillValue(prefillValue)) return;

          updates[`${targetPath}.${prefillField}`] = prefillValue;
          updates[`${targetPath}.${timestampField}`] = currentTimestamp;
        });
      });
    }
  }

  // Write all updates atomically if there are any. Write failures are logged
  // AND rethrown: the caller (useAutismPrefillSync) retries once, because a
  // silently dropped prefill write means questionnaire answers never reach the
  // clinician proforma or the LLM report pipeline's stored inputs.
  if (Object.keys(updates).length > 0) {
    try {
      await updateDoc(historyStageRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('[AutismPrefillFunctions] Failed to write prefill:', err);
      throw err;
    }
  }
}

/**
 * Determines whether a form instance has any questions with prefill metadata.
 * Used as a fast check to avoid unnecessary shim construction and prefill runs.
 *
 * @param {object} formInstance - Form instance with formDefinition
 * @returns {boolean}
 */
export function formHasPrefillMetadata(formInstance) {
  if (!formInstance?.formDefinition?.formSubsections) return false;

  for (const subsection of Object.values(formInstance.formDefinition.formSubsections)) {
    for (const question of Object.values(subsection.questions || {})) {
      if (question.prefillsToModules && question.prefillsToStages && question.prefillsToProviderVarNames) {
        return true;
      }
    }
  }
  return false;
}

// ─── History display fallback (client-side constructed prefill) ─────────────

/**
 * Merge all form instances into one existingData.formData tree so composite
 * handlers (alcohol, sleep, etc.) can look up values across the assessment.
 */
export function buildMergedFormDataShim(formInstances) {
  const merged = { formData: {} };

  Object.entries(formInstances || {}).forEach(([formKey, formInstance]) => {
    const shim = buildFormDataShimFromInstance(formKey, formInstance);
    if (shim.formData?.[formKey]) {
      merged.formData[formKey] = shim.formData[formKey];
    }
  });

  return merged;
}

/**
 * Informant form subquestions use an `informant_` prefix on var names, but the
 * shared PrefillFunctions composite handlers look up patient var names
 * (e.g. doesDrinkAlcohol). Duplicate each informant answer under the patient
 * var name so handlers work without informant-specific copies.
 */
function cloneAndAddInformantVarAliases(existingData) {
  const cloned = JSON.parse(JSON.stringify(existingData));

  Object.values(cloned.formData || {}).forEach((form) => {
    Object.values(form.formSubsections || {}).forEach((subsection) => {
      Object.values(subsection.questions || {}).forEach((question) => {
        const subquestions = question.subquestions || {};
        const aliases = {};

        Object.entries(subquestions).forEach(([sqKey, sq]) => {
          const varName = sq?.serviceUserVarName;
          if (!varName?.startsWith('informant_')) return;

          const patientVar = varName.slice('informant_'.length);
          aliases[`${sqKey}_patientAlias`] = {
            ...sq,
            serviceUserVarName: patientVar,
          };
        });

        Object.assign(subquestions, aliases);
      });
    });
  });

  return cloned;
}

/**
 * Locate the form question whose prefill metadata targets a history providerVarName.
 */
export function findSourceFormQuestionForProviderVar(
  formInstances,
  providerVarName,
  patientFirstName = '',
) {
  if (!providerVarName || !formInstances) return null;

  for (const [formKey, formInstance] of Object.entries(formInstances)) {
    const displayInstance = patientFirstName
      ? resolveFormInstanceForDisplay(formInstance, patientFirstName)
      : formInstance;

    const formDef = displayInstance?.formDefinition;
    if (!formDef?.formSubsections) continue;

    for (const [subKey, subsection] of Object.entries(formDef.formSubsections)) {
      for (const [qKey, question] of Object.entries(subsection.questions || {})) {
        const targets = question.prefillsToProviderVarNames;
        if (!targets) continue;

        const names = Array.isArray(targets) ? targets : [targets];
        if (names.includes(providerVarName)) {
          return {
            formKey,
            subKey,
            qKey,
            question,
            formInstance,
          };
        }
      }
    }
  }

  return null;
}

/**
 * Format a history line item using the same constructed prefill handlers as the
 * ADHD assessment / AutismPrefillFunctions pipeline. Used when patientPrefill or
 * informantPrefill is not yet stored on the history stage (e.g. demo patients).
 *
 * @param {string} providerVarName - History question providerVarName (e.g. "alcohol")
 * @param {object} formInstances - formKey -> formInstance map for patient or informant
 * @param {'patient'|'informant'} [actorType='patient']
 * @param {string} [patientFirstName] - Resolves {{patientFirstName}} in informant schemas
 * @returns {string|null}
 */
export function formatHistoryPrefillFromFormInstances(
  providerVarName,
  formInstances,
  actorType = 'patient',
  patientFirstName = '',
) {
  const source = findSourceFormQuestionForProviderVar(
    formInstances,
    providerVarName,
    patientFirstName,
  );
  if (!source) return null;

  let existingData = buildMergedFormDataShim(formInstances);
  if (actorType === 'informant') {
    existingData = cloneAndAddInformantVarAliases(existingData);
  }

  const { formKey, subKey, qKey, question: formQuestion, formInstance } = source;
  const allSubquestions = formQuestion.subquestions || {};
  const answers = formInstance.answers || {};
  const questionAnswers = answers[qKey] || {};

  const orderedSubquestions = Object.entries(allSubquestions)
    .filter(([sqKey]) => !Number.isNaN(parseInt(sqKey, 10)))
    .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
    .map(([, sq]) => sq);

  const triggerVarName = orderedSubquestions[0]?.serviceUserVarName;
  const triggerAnswer = triggerVarName
    ? (questionAnswers[triggerVarName] ?? answers[triggerVarName])
    : null;

  return formatPrefillValue(
    providerVarName,
    triggerAnswer,
    allSubquestions,
    existingData,
    formKey,
    subKey,
    qKey,
  );
}
